import { QueryHookOptions, useApolloClient } from "@apollo/client";
import { ForwardedRef, forwardRef, useCallback } from "react";
import {
  GroupBase,
  OptionProps,
  SelectInstance,
  components,
} from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useAsyncFetchOptions from "components/Shared/Select/useAsyncFetchOptions";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertBrandId, assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useSelectStyles, { SelectThemingProps } from "utils/useSelectStyles";
import { gql, getFragmentData, DocumentType } from "__generated__/gql";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { RetailerFilter } from "__generated__/gql/graphql";
import useLatestRef from "utils/useLatestRef";
import { NameWithFlagIcons } from "components/Shared/RetailerDirectory/utils";
import RetailerBrokerIcons, {
  RetailerBrokerIconsFragment,
} from "components/Shared/RetailerDirectory/RetailerBrokerIcons";
import { isNewOption } from "components/Shared/Select/utils";

const OPTION_LIMIT = 15;

const optionFragment = gql(/* GraphQL */ `
  fragment RetailerSelectOption on Retailer {
    id
    nameWithUniqueSuffix
    ...RetailerNameWithFlagIconsFragment
  }
`);

const valueQuery = gql(/* GraphQL */ `
  query RetailerSelectValue($ids: [UUID!]!) {
    retailersList(filter: { id: { in: $ids } }) {
      ...RetailerSelectOption
    }
  }
`);

const query = gql(/* GraphQL */ `
  query RetailerSelectOptions(
    $brandId: UUID!
    $filter: RetailerFilter
    $limit: Int = 5
  ) {
    retailersForBrand(
      brandId: $brandId
      filter: $filter
      orderBy: NAME_ASC
      first: $limit
    ) {
      totalCount
      nodes {
        ...RetailerSelectOption
        ...RetailerBrokerIcons
      }
    }
  }
`);

const adminQuery = gql(/* GraphQL */ `
  query AdminRetailerSelectOptions($filter: RetailerFilter, $limit: Int = 5) {
    retailers(
      condition: { forBrandId: null }
      filter: $filter
      orderBy: NAME_ASC
      first: $limit
    ) {
      totalCount
      nodes {
        ...RetailerSelectOption
      }
    }
  }
`);

export type Option = DocumentType<typeof optionFragment>;

function noOptionsMessage({ inputValue }: { inputValue: string }) {
  if (inputValue?.length > 1)
    return <>No retailers found containing: “{inputValue}”</>;
  return "Enter at least two characters to search…";
}

function RetailerOption(props: OptionProps<Option>) {
  const { data } = props;

  if (isNewOption(data)) {
    return <components.Option {...props}>{data.label}</components.Option>;
  }

  return (
    <components.Option {...props}>
      <NameWithFlagIcons
        retailer={data}
        noOfLines={Infinity}
        lineHeight="normal"
      >
        <RetailerBrokerIcons retailer={data as RetailerBrokerIconsFragment} />
      </NameWithFlagIcons>
    </components.Option>
  );
}

export type RetailerSelectProps<IsMulti extends boolean = false> = Omit<
  AsyncProps<Option, IsMulti, GroupBase<Option>>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti> & {
    valueQueryOptions?: Omit<
      QueryHookOptions<
        DocumentType<typeof valueQuery>,
        DocumentVariablesType<typeof valueQuery>
      >,
      "variables"
    >;
  } & {
    isAdmin?: boolean;
    filter?: RetailerFilter;
    themingProps?: SelectThemingProps;
  };

export function useRetailerSelect<IsMulti extends boolean = false>({
  components: componentsProp,
  isAdmin,
  isMulti,
  filter: filterProp,
  onChange,
  styles,
  themingProps,
  value: idValue,
  valueQueryOptions,
  ...rest
}: RetailerSelectProps<IsMulti>) {
  const styleProps = useSelectStyles<Option, IsMulti>(styles, themingProps);
  const client = useApolloClient();
  const brandId = useCurrentBrandId();

  const idValueProps = useIdValue<Option, IsMulti, typeof valueQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.retailersList) as Option[],
    isMulti,
    labelKey: "nameWithUniqueSuffix",
    onChange,
    query: valueQuery,
    queryOptions: valueQueryOptions,
    value: idValue,
  });

  const latestFilterPropRef = useLatestRef(filterProp);

  const asyncFetchProps = useAsyncFetchOptions({
    cacheUsing: filterProp,
    fetchOptions: useCallback(
      (inputValue) => {
        const baseFilter: RetailerFilter = /^[0-9a-f-]{32,36}$/i.test(
          inputValue,
        )
          ? { id: { equalTo: inputValue } }
          : {
              nameWithUniqueSuffix: { includesInsensitive: inputValue },
              isArchived: { equalTo: false },
            };

        const filter: RetailerFilter = latestFilterPropRef.current
          ? { and: [baseFilter, latestFilterPropRef.current] }
          : baseFilter;

        if (isAdmin) {
          return client
            .query({
              query: adminQuery,
              variables: { filter, limit: OPTION_LIMIT },
              fetchPolicy: "network-only",
              context: ADMIN_CONTEXT,
            })
            .then(({ data }) => {
              assertPresence(
                data.retailers,
                "Could not get retailers from response.",
              );
              const { nodes, totalCount } = data.retailers;
              const options = getFragmentData(optionFragment, nodes);
              return { options, totalCount };
            });
        }

        assertBrandId(brandId);

        return client
          .query({
            query,
            variables: { brandId, filter },
            fetchPolicy: "network-only",
            context: getActiveBrandIdContext(brandId),
          })
          .then(({ data }) => {
            assertPresence(
              data.retailersForBrand,
              "Could not get retailers from response.",
            );
            const { nodes, totalCount } = data.retailersForBrand;
            const options = getFragmentData(optionFragment, nodes);
            return { options, totalCount };
          });
      },
      [isAdmin, brandId, client, latestFilterPropRef],
    ),
  });

  return {
    ...styleProps,
    ...idValueProps,
    ...asyncFetchProps,
    isMulti,
    noOptionsMessage,
    components: {
      ...styleProps.components,
      ...asyncFetchProps.components,
      Option: RetailerOption,
      ...componentsProp,
    },
    ...rest,
  };
}

export type RetailerSelectInstance<IsMulti extends boolean = false> =
  SelectInstance<Option, IsMulti>;

function RetailerSelectInner<IsMulti extends boolean = false>(
  props: RetailerSelectProps<IsMulti>,
  ref: ForwardedRef<RetailerSelectInstance<IsMulti>>,
) {
  const selectProps = useRetailerSelect<IsMulti>(props);

  return <AsyncSelect<Option, IsMulti> ref={ref} {...selectProps} />;
}

export default forwardRef(RetailerSelectInner);
